import React, { useState, useEffect, useRef } from "react";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const isSameDay = (a, b) => a && b && a.toDateString() === b.toDateString();

export function formatCalendarLabel(dateStart, dateEnd) {
  const formatDate = (d) =>
    d
      ? `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`
      : "";
  if (!dateStart) return "";
  if (dateEnd) return `${formatDate(dateStart)} – ${formatDate(dateEnd)}`;
  return formatDate(dateStart);
}

export default function CustomCalendar({
  dateStart,
  setDateStart,
  dateEnd,
  setDateEnd,
  onClose,
  position = "bottom",
  disableMode = "past",
  inline = false,
  overlay = false,
  minDate = null,
  singleDateMode = false,
  selectsRangeEnd = false,
}) {
  const [calViewDate, setCalViewDate] = useState(dateStart || new Date());
  const [hoverDate, setHoverDate] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (overlay || inline) return;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        if (onClose) onClose();
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener("click", handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("click", handleClickOutside);
    };
  }, [overlay, inline, onClose]);

  const isInRange = (d) => {
    if (!dateStart) return false;
    const end = dateEnd || hoverDate;
    if (!end) return false;
    const [lo, hi] = dateStart <= end ? [dateStart, end] : [end, dateStart];
    return d > lo && d < hi;
  };

  const handleDayClick = (d) => {
    if (selectsRangeEnd) {
      if (setDateEnd) setDateEnd(d);
      if (onClose) onClose();
      return;
    }

    if (singleDateMode) {
      setDateStart(d);
      if (setDateEnd) setDateEnd(d);
      if (onClose) onClose();
      return;
    }

    if (!dateStart || (dateStart && dateEnd)) {
      setDateStart(d);
      setDateEnd(null);
    } else {
      if (d < dateStart) {
        setDateEnd(dateStart);
        setDateStart(d);
      } else {
        setDateEnd(d);
      }
      if (onClose) onClose(); // Auto close after range selected
    }
  };

  const getDaysInMonth = (year, month) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const year = calViewDate.getFullYear();
  const month = calViewDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const containerClass = inline
    ? "relative mt-2 bg-white border border-gray-200 rounded-xl p-3 w-full max-w-[260px] mx-auto"
    : overlay
      ? "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[200] bg-white border border-gray-200 rounded-2xl shadow-2xl p-4 w-[280px] sm:w-[300px]"
      : `absolute z-[100] bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-[260px] ${position === "top" ? "bottom-full mb-1" : "top-full mt-1"}`;

  const calendarBody = (
    <>
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setCalViewDate(new Date(year, month - 1, 1))}
          className="p-1 rounded hover:bg-gray-100 text-gray-500 text-sm"
        >
          ‹
        </button>
        <span className="text-[13px] font-semibold text-gray-700">
          {MONTHS[month]} {year}
        </span>
        <button
          type="button"
          onClick={() => setCalViewDate(new Date(year, month + 1, 1))}
          className="p-1 rounded hover:bg-gray-100 text-gray-500 text-sm"
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 mb-1">
        {DAYS.map((d) => (
          <span
            key={d}
            className="text-center text-[10px] text-gray-400 font-medium py-0.5"
          >
            {d}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((d, i) => {
          if (!d) return <span key={`e${i}`} />;
          const isStart = isSameDay(d, dateStart);
          const isEnd = isSameDay(d, dateEnd);
          const inRange = !singleDateMode && isInRange(d);
          const isPast = d < today;
          const isFuture = d > today;
          const normalizeDay = (dt) => (dt ? new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime() : null);
          const currentDayMs = normalizeDay(d);
          const minDateMs = normalizeDay(minDate);
          const isBeforeMin = minDateMs !== null ? currentDayMs < minDateMs : false;

          const isDisabled =
            minDate
              ? (isBeforeMin && !isStart)
              : disableMode === "past"
                ? (isPast && !isStart)
                : disableMode === "future"
                  ? isFuture
                  : false;
          return (
            <button
              key={d.toISOString()}
              type="button"
              disabled={isDisabled}
              onMouseEnter={() => !singleDateMode && dateStart && (selectsRangeEnd || !dateEnd) && setHoverDate(d)}
              onMouseLeave={() => setHoverDate(null)}
              onClick={() => !isDisabled && handleDayClick(d)}
              className={`text-[11px] h-7 w-full rounded-md transition-all
                ${isDisabled ? "text-gray-300 cursor-not-allowed" : "cursor-pointer hover:bg-orange-50"}
                ${isStart || isEnd ? "bg-[#FF552E] text-white font-bold hover:bg-[#e64621]" : ""}
                ${inRange ? "bg-orange-100 text-orange-700 rounded-none" : ""}
              `}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-gray-400 mt-2 text-center">
        {singleDateMode
          ? (selectsRangeEnd ? "Select end date" : "Select date")
          : !dateStart
            ? "Select start date"
            : !dateEnd
              ? "Select end date (or click again for single day)"
              : (
                <span
                  onClick={() => {
                    setDateStart(null);
                    setDateEnd(null);
                    if (onClose) onClose();
                  }}
                  className="cursor-pointer text-[#FF552E] hover:underline font-semibold"
                >
                  Click to reset
                </span>
              )}
      </p>
    </>
  );

  if (overlay) {
    return (
      <>
        <div className="fixed inset-0 z-[190]" onClick={onClose}></div>
        <div ref={containerRef} className={containerClass}>{calendarBody}</div>
      </>
    );
  }

  return <div ref={containerRef} className={containerClass}>{calendarBody}</div>;
}
